import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import {
  AccessTime,
  Assessment,
  CheckCircle,
  Groups,
  Refresh,
  Search,
  Speed,
  Timer,
} from "@mui/icons-material";

import CertificateService from "../../../services/certificateServices";

// =====================================================
// HELPERS
// =====================================================

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    "Something went wrong. Please try again."
  );
};

// =====================================================
// FORMAT DURATION
// =====================================================

const formatDuration = (seconds) => {
  if (
    seconds === null ||
    seconds === undefined ||
    seconds === "" ||
    Number.isNaN(Number(seconds))
  ) {
    return "-";
  }

  const totalSeconds = Math.max(
    0,
    Math.round(Number(seconds))
  );

  const hours = Math.floor(
    totalSeconds / 3600
  );

  const minutes = Math.floor(
    (totalSeconds % 3600) / 60
  );

  const remainingSeconds =
    totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  }

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }

  return `${remainingSeconds}s`;
};

// =====================================================
// FORMAT ROLE
// =====================================================

const formatRole = (role) => {
  if (!role) {
    return "-";
  }

  switch (String(role).toLowerCase()) {
    case "certificateexecutive":
      return "Certificate Executive";

    case "certificateteamlead":
      return "Certificate Team Lead";

    case "certificateadmin":
      return "Certificate Admin";

    case "superadmin":
      return "Super Admin";

    default:
      return String(role)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) =>
          char.toUpperCase()
        );
  }
};

// =====================================================
// ROLE COLOR
// =====================================================

const getRoleColor = (role) => {
  switch (String(role || "").toLowerCase()) {
    case "certificateexecutive":
      return "primary";

    case "certificateteamlead":
      return "info";

    case "certificateadmin":
      return "success";

    case "superadmin":
      return "warning";

    default:
      return "default";
  }
};

// =====================================================
// AGENT NAME
// =====================================================

const getAgentName = (row) => {
  return row?.agent_name || "-";
};

// =====================================================
// AGENT ID
// =====================================================

const getAgentId = (row) => {
  return row?.agent_id ?? "-";
};

// =====================================================
// COMPLETED COUNT
// =====================================================

const getCompletedCount = (row) => {
  return Number(row?.completed_count ?? 0);
};

// =====================================================
// AVERAGE SECONDS
// =====================================================

const getAverageSeconds = (row) => {
  return row?.avg_seconds_per_task ?? null;
};

// =====================================================
// MAIN COMPONENT
// =====================================================

const CertificateAgentPerformance = () => {
  // ===================================================
  // STATE
  // ===================================================

  const [performanceData, setPerformanceData] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [refreshing, setRefreshing] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [page, setPage] =
    useState(0);

  const [rowsPerPage, setRowsPerPage] =
    useState(10);

  const [snackbar, setSnackbar] =
    useState({
      open: false,
      message: "",
      severity: "success",
    });

  // ===================================================
  // SHOW MESSAGE
  // ===================================================

  const showMessage = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // ===================================================
  // LOAD PERFORMANCE
  // ===================================================

  const loadPerformance = useCallback(
    async (isRefresh = false) => {
      try {
        if (isRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const response =
          await CertificateService.getCertificateAgentPerformance(
            {}
          );

        /*
         * Actual API response:
         *
         * {
         *   "count": 3,
         *   "results": [
         *     {
         *       "agent_id": 1837,
         *       "agent_name": "vicky",
         *       "role": "certificateteamlead",
         *       "completed_count": 0,
         *       "avg_seconds_per_task": null
         *     }
         *   ]
         * }
         */

        const results =
          Array.isArray(
            response?.data?.results
          )
            ? response.data.results
            : [];

        setPerformanceData(results);

        if (isRefresh) {
          showMessage(
            "Performance data refreshed successfully."
          );
        }
      } catch (error) {
        console.error(
          "Agent performance error:",
          error
        );

        setPerformanceData([]);

        showMessage(
          getErrorMessage(error),
          "error"
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  // ===================================================
  // INITIAL LOAD
  // ===================================================

  useEffect(() => {
    loadPerformance();
  }, [loadPerformance]);

  // ===================================================
  // SEARCH
  // ===================================================

  const filteredData = useMemo(() => {
    const searchValue =
      search.trim().toLowerCase();

    if (!searchValue) {
      return performanceData;
    }

    return performanceData.filter(
      (row) => {
        const agentName =
          String(
            row?.agent_name || ""
          ).toLowerCase();

        const agentId =
          String(
            row?.agent_id || ""
          ).toLowerCase();

        const role =
          formatRole(
            row?.role
          ).toLowerCase();

        return (
          agentName.includes(
            searchValue
          ) ||
          agentId.includes(
            searchValue
          ) ||
          role.includes(
            searchValue
          )
        );
      }
    );
  }, [
    performanceData,
    search,
  ]);

  // ===================================================
  // SUMMARY
  // ===================================================

  const summary = useMemo(() => {
    const totalAgents =
      performanceData.length;

    const totalCompletedJobs =
      performanceData.reduce(
        (total, row) =>
          total +
          getCompletedCount(row),
        0
      );

    const agentsWithPerformance =
      performanceData.filter(
        (row) =>
          row?.avg_seconds_per_task !==
            null &&
          row?.avg_seconds_per_task !==
            undefined
      ).length;

    const validAverageRows =
      performanceData.filter(
        (row) => {
          const value =
            row?.avg_seconds_per_task;

          return (
            value !== null &&
            value !== undefined &&
            !Number.isNaN(
              Number(value)
            )
          );
        }
      );

    let overallAverage = null;

    if (validAverageRows.length > 0) {
      const totalSeconds =
        validAverageRows.reduce(
          (total, row) =>
            total +
            Number(
              row.avg_seconds_per_task
            ),
          0
        );

      overallAverage =
        totalSeconds /
        validAverageRows.length;
    }

    return {
      totalAgents,
      totalCompletedJobs,
      agentsWithPerformance,
      overallAverage,
    };
  }, [performanceData]);

  // ===================================================
  // PAGINATION
  // ===================================================

  const paginatedData =
    useMemo(() => {
      const start =
        page * rowsPerPage;

      return filteredData.slice(
        start,
        start + rowsPerPage
      );
    }, [
      filteredData,
      page,
      rowsPerPage,
    ]);

  // ===================================================
  // SEARCH CHANGE
  // ===================================================

  const handleSearchChange = (
    event
  ) => {
    setSearch(
      event.target.value
    );

    setPage(0);
  };

  // ===================================================
  // PAGE CHANGE
  // ===================================================

  const handleChangePage = (
    event,
    newPage
  ) => {
    setPage(newPage);
  };

  // ===================================================
  // ROWS PER PAGE
  // ===================================================

  const handleChangeRowsPerPage = (
    event
  ) => {
    setRowsPerPage(
      parseInt(
        event.target.value,
        10
      )
    );

    setPage(0);
  };

  // ===================================================
  // SUMMARY CARD
  // ===================================================

  const SummaryCard = ({
    title,
    value,
    subtitle,
    icon,
  }) => {
    return (
      <Card
        elevation={0}
        sx={{
          height: "100%",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
        }}
      >
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={600}
              >
                {title}
              </Typography>

              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  mt: 1,
                }}
              >
                {value}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {subtitle}
              </Typography>
            </Box>

            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor:
                  "action.hover",
              }}
            >
              {icon}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // ===================================================
  // RENDER
  // ===================================================

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          md: 3,
        },
        backgroundColor:
          "#f7f8fa",
        minHeight: "100vh",
      }}
    >
      {/* =================================================
          HEADER
      ================================================= */}

      <Box
        sx={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: {
            xs: "flex-start",
            md: "center",
          },
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={800}
          >
            Executive Performance
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
            }}
          >
            View certificate team
            member performance and
            completion statistics.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={
            refreshing ? (
              <CircularProgress
                size={18}
              />
            ) : (
              <Refresh />
            )
          }
          onClick={() =>
            loadPerformance(true)
          }
          disabled={
            loading ||
            refreshing
          }
        >
          Refresh
        </Button>
      </Box>

      {/* =================================================
          SUMMARY CARDS
      ================================================= */}

      <Grid
        container
        spacing={2}
        sx={{
          mb: 3,
        }}
      >
        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >
          <SummaryCard
            title="Total Members"
            value={
              summary.totalAgents
            }
            subtitle="Team members returned by API"
            icon={
              <Groups />
            }
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >
          <SummaryCard
            title="Completed Jobs"
            value={
              summary.totalCompletedJobs
            }
            subtitle="Approved jobs"
            icon={
              <CheckCircle />
            }
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >
          <SummaryCard
            title="Performance Available"
            value={
              summary.agentsWithPerformance
            }
            subtitle="Members with timing data"
            icon={
              <Speed />
            }
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >
          <SummaryCard
            title="Average Time"
            value={
              summary.overallAverage !==
              null
                ? formatDuration(
                    summary.overallAverage
                  )
                : "-"
            }
            subtitle="Available performance data"
            icon={
              <Timer />
            }
          />
        </Grid>
      </Grid>

      {/* =================================================
          MAIN TABLE
      ================================================= */}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {/* =================================================
            TOOLBAR
        ================================================= */}

        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: {
              xs: "stretch",
              md: "center",
            },
            flexDirection: {
              xs: "column",
              md: "row",
            },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
            >
              Certificate Team Performance
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              Showing data returned by
              the agent performance API.
            </Typography>
          </Box>

          <TextField
            size="small"
            placeholder="Search by name, ID or role..."
            value={search}
            onChange={
              handleSearchChange
            }
            sx={{
              width: {
                xs: "100%",
                md: 320,
              },
            }}
            InputProps={{
              startAdornment: (
                <Search
                  sx={{
                    mr: 1,
                    color:
                      "text.secondary",
                  }}
                />
              ),
            }}
          />
        </Box>

        <Divider />

        {/* =================================================
            LOADING
        ================================================= */}

        {loading ? (
          <Box
            sx={{
              minHeight: 350,
              display: "flex",
              alignItems:
                "center",
              justifyContent:
                "center",
            }}
          >
            <Stack
              alignItems="center"
              spacing={2}
            >
              <CircularProgress />

              <Typography
                color="text.secondary"
              >
                Loading performance data...
              </Typography>
            </Stack>
          </Box>
        ) : filteredData.length ===
          0 ? (
          /* =================================================
              EMPTY
          ================================================= */

          <Box
            sx={{
              minHeight: 350,
              display: "flex",
              alignItems:
                "center",
              justifyContent:
                "center",
              flexDirection:
                "column",
              gap: 1,
              p: 3,
            }}
          >
            <Assessment
              sx={{
                fontSize: 52,
                color:
                  "text.disabled",
              }}
            />

            <Typography
              variant="h6"
              color="text.secondary"
            >
              No performance data found
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {search
                ? "No team member matches your search."
                : "No certificate team performance data is available."}
            </Typography>
          </Box>
        ) : (
          <>
            {/* =================================================
                TABLE
            ================================================= */}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>
                        S.No.
                      </strong>
                    </TableCell>

                    <TableCell>
                      <strong>
                        Agent ID
                      </strong>
                    </TableCell>

                    <TableCell>
                      <strong>
                        Agent Name
                      </strong>
                    </TableCell>

                    <TableCell>
                      <strong>
                        Role
                      </strong>
                    </TableCell>

                    <TableCell align="center">
                      <strong>
                        Completed Jobs
                      </strong>
                    </TableCell>

                    <TableCell align="center">
                      <strong>
                        Average Time
                      </strong>
                    </TableCell>

                    <TableCell align="center">
                      <strong>
                        Performance Status
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedData.map(
                    (row, index) => {
                      const completedCount =
                        getCompletedCount(
                          row
                        );

                      const averageSeconds =
                        getAverageSeconds(
                          row
                        );

                      const hasPerformance =
                        averageSeconds !==
                          null &&
                        averageSeconds !==
                          undefined;

                      return (
                        <TableRow
                          key={
                            row?.agent_id ??
                            `${page}-${index}`
                          }
                          hover
                        >
                          {/* S.NO */}
                          <TableCell>
                            {page *
                              rowsPerPage +
                              index +
                              1}
                          </TableCell>

                          {/* AGENT ID */}
                          <TableCell>
                            <Typography
                              fontWeight={600}
                            >
                              {getAgentId(
                                row
                              )}
                            </Typography>
                          </TableCell>

                          {/* AGENT NAME */}
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                            >
                              {/* <Box
                                sx={{
                                  width: 38,
                                  height: 38,
                                  borderRadius:
                                    "50%",
                                  display:
                                    "flex",
                                  alignItems:
                                    "center",
                                  justifyContent:
                                    "center",
                                  backgroundColor:
                                    "action.hover",
                                  fontWeight: 700,
                                  textTransform:
                                    "uppercase",
                                }}
                              >
                                {getAgentName(
                                  row
                                )
                                  }
                              </Box> */}

                              <Typography
                                fontWeight={700}
                              >
                                {getAgentName(
                                  row
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>

                          {/* ROLE */}
                          <TableCell>
                            <Chip
                              size="small"
                              label={formatRole(
                                row?.role
                              )}
                              color={getRoleColor(
                                row?.role
                              )}
                              variant="outlined"
                            />
                          </TableCell>

                          {/* COMPLETED JOBS */}
                          <TableCell align="center">
                            <Chip
                              size="small"
                              icon={
                                <CheckCircle />
                              }
                              label={
                                completedCount
                              }
                              color={
                                completedCount >
                                0
                                  ? "success"
                                  : "default"
                              }
                              variant="outlined"
                            />
                          </TableCell>

                          {/* AVERAGE TIME */}
                          <TableCell align="center">
                            <Stack
                              direction="row"
                              justifyContent="center"
                              alignItems="center"
                              spacing={0.5}
                            >
                              <AccessTime
                                fontSize="small"
                                color="action"
                              />

                              <Typography
                                fontWeight={700}
                              >
                                {hasPerformance
                                  ? formatDuration(
                                      averageSeconds
                                    )
                                  : "-"}
                              </Typography>
                            </Stack>
                          </TableCell>

                          {/* PERFORMANCE STATUS */}
                          <TableCell align="center">
                            {hasPerformance ? (
                              <Chip
                                size="small"
                                icon={
                                  <Speed />
                                }
                                label="Available"
                                color="success"
                              />
                            ) : (
                              <Chip
                                size="small"
                                label="No Performance Data"
                                color="default"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* =================================================
                PAGINATION
            ================================================= */}

            <TablePagination
              component="div"
              count={
                filteredData.length
              }
              page={page}
              onPageChange={
                handleChangePage
              }
              rowsPerPage={
                rowsPerPage
              }
              onRowsPerPageChange={
                handleChangeRowsPerPage
              }
              rowsPerPageOptions={[
                5,
                10,
                25,
                50,
              ]}
            />
          </>
        )}
      </Paper>

      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar({
            ...snackbar,
            open: false,
          })
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          onClose={() =>
            setSnackbar({
              ...snackbar,
              open: false,
            })
          }
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CertificateAgentPerformance;